/**
 * @fileoverview Native select — catalyst Select adapted for light-mode intake forms.
 *
 * Uses a native <select> with custom chevron overlay.
 * For searchable selects, use the Combobox from catalyst directly.
 */
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import React, { forwardRef } from 'react';

export interface FormSelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  /** Placeholder text shown as first disabled option */
  placeholder?: string;
}

export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  ({ className, invalid, placeholder, children, ...props }, ref) => (
    <span
      data-slot="control"
      className={clsx(
        'relative block w-full',
        // Inset pseudo
        'before:absolute before:inset-px before:rounded-[7px] before:bg-white before:shadow-sm',
        // Focus ring
        'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset',
        'has-[:focus]:after:ring-2 has-[:focus]:after:ring-blue-500',
        // Disabled (target the select element directly, not any descendant like <option disabled>)
        'has-[>select:disabled]:opacity-50 has-[>select:disabled]:before:bg-zinc-50 has-[>select:disabled]:before:shadow-none',
      )}
    >
      <select
        ref={ref}
        className={clsx(
          className,
          // Layout
          'relative block w-full appearance-none rounded-lg',
          'py-2 pl-3 pr-9 sm:py-1.5',
          // Typography
          'text-sm text-zinc-900',
          // Border
          'border border-zinc-200',
          'hover:border-zinc-300',
          // Background
          'bg-transparent',
          // Focus
          'focus:outline-none',
          // Invalid
          invalid && 'border-red-400 hover:border-red-400',
          // Disabled
          'disabled:border-zinc-200 disabled:cursor-not-allowed',
        )}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {children}
      </select>
      {/* Chevron */}
      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
        <ChevronDown className="h-4 w-4 text-zinc-400" />
      </span>
    </span>
  ),
);

FormSelect.displayName = 'FormSelect';
